import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { APP_CONFIG } from '../data/content';

const BusinessIntelligencePage = () => {
    const navigate = useNavigate();

    return (
        <PageTransition>
            <div className="bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 min-h-screen font-inter selection:bg-violet-500/30 selection:text-violet-200 transition-colors duration-300">
                <Header projectName={APP_CONFIG.projectName} />

                {/* Hero Section */}
                <section className="relative py-24 px-6 overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-950/20 via-slate-50 dark:via-slate-950 to-slate-50 dark:to-slate-950">
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20 dark:opacity-30" />
                    
                    <motion.div 
                        className="max-w-4xl mx-auto text-center relative z-10"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <motion.div 
                            className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-violet-500 to-fuchsia-400 flex items-center justify-center text-3xl mx-auto mb-6 shadow-lg shadow-violet-500/20"
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
                        >
                            📊
                        </motion.div>
                        <h1 className="text-4xl sm:text-5xl font-extrabold mb-6 font-outfit tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 dark:from-white dark:via-slate-100 dark:to-violet-400">
                            Business Intelligence
                        </h1>
                        <p className="text-xl sm:text-2xl text-slate-600 dark:text-slate-400 mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                            Transform data into actionable insights with RAG-powered analytics that understand your business context.
                        </p>
                    </motion.div>
                </section>

                {/* Main Content */}
                <section className="py-16 px-6 relative">
                    <div className="max-w-4xl mx-auto space-y-12">
                        
                        {/* Overview */}
                        <motion.div 
                            className="bg-white dark:bg-slate-900/40 backdrop-blur-md p-8 sm:p-10 rounded-2xl border border-slate-200 dark:border-slate-800/60 shadow-lg dark:shadow-none"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-2xl sm:text-3xl font-bold mb-6 font-outfit bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-violet-600 dark:from-white dark:to-violet-400">Overview</h2>
                            <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-6 font-light">
                                Traditional business intelligence tools require complex queries, technical expertise, and significant time to extract insights. Our AI-powered BI solution revolutionizes how organizations interact with their data by enabling natural language queries, contextual understanding, and automated insight generation.
                            </p>
                            <p className="text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                                By combining retrieval-augmented generation with your business data, our system understands not just what you're asking, but why you're asking it. It provides context-aware analysis, predictive insights, and actionable recommendations in seconds.
                            </p>
                        </motion.div>

                        {/* Key Capabilities */}
                        <motion.div 
                            className="bg-white dark:bg-slate-900/40 backdrop-blur-md p-8 sm:p-10 rounded-2xl border border-slate-200 dark:border-slate-800/60 shadow-lg dark:shadow-none"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-2xl sm:text-3xl font-bold mb-8 font-outfit bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-violet-600 dark:from-white dark:to-violet-400">Key Capabilities</h2>
                            <div className="grid gap-8">
                                <div className="flex items-start gap-5 group">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-violet-50 dark:bg-violet-950/50 border border-violet-100 dark:border-violet-800/30 flex items-center justify-center text-xl group-hover:bg-violet-100/30 transition-colors text-violet-600 dark:text-violet-400">
                                        💬
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100 font-outfit">Natural Language Queries</h3>
                                        <p className="text-slate-600 dark:text-slate-400 font-light leading-relaxed">
                                            Ask questions in plain English and get instant answers. No SQL knowledge required. Our AI understands business terminology and translates it into complex data operations automatically.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-5 group">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-violet-50 dark:bg-violet-950/50 border border-violet-100 dark:border-violet-800/30 flex items-center justify-center text-xl group-hover:bg-violet-100/30 transition-colors text-violet-600 dark:text-violet-400">
                                        ⚡
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100 font-outfit">Real-Time Analysis</h3>
                                        <p className="text-slate-600 dark:text-slate-400 font-light leading-relaxed">
                                            Connect to live data sources and get up-to-the-second insights. Monitor KPIs, track trends, and identify anomalies as they happen with automated alerting.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-5 group">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-violet-50 dark:bg-violet-950/50 border border-violet-100 dark:border-violet-800/30 flex items-center justify-center text-xl group-hover:bg-violet-100/30 transition-colors text-violet-600 dark:text-violet-400">
                                        🔮
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100 font-outfit">Predictive Analytics</h3>
                                        <p className="text-slate-600 dark:text-slate-400 font-light leading-relaxed">
                                            Leverage machine learning models to forecast trends, predict customer behavior, and identify opportunities before your competitors do.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-5 group">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-violet-50 dark:bg-violet-950/50 border border-violet-100 dark:border-violet-800/30 flex items-center justify-center text-xl group-hover:bg-violet-100/30 transition-colors text-violet-600 dark:text-violet-400">
                                        📈
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100 font-outfit">Automated Reporting</h3>
                                        <p className="text-slate-600 dark:text-slate-400 font-light leading-relaxed">
                                            Generate comprehensive reports automatically. Schedule daily, weekly, or monthly reports that adapt to your business needs and highlight what matters most.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Use Cases */}
                        <motion.div 
                            className="bg-white dark:bg-slate-900/40 backdrop-blur-md p-8 sm:p-10 rounded-2xl border border-slate-200 dark:border-slate-800/60 shadow-lg dark:shadow-none"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-2xl sm:text-3xl font-bold mb-8 font-outfit bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-violet-600 dark:from-white dark:to-violet-400">Real-World Applications</h2>
                            <div className="space-y-6">
                                <div className="border-l-2 border-violet-500 pl-6 group">
                                    <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100 font-outfit group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">Sales Performance Analysis</h3>
                                    <p className="text-slate-600 dark:text-slate-400 font-light leading-relaxed">
                                        Track pipeline health, forecast revenue, identify top performers, and understand conversion patterns across different segments and channels.
                                    </p>
                                </div>
                                <div className="border-l-2 border-violet-500 pl-6 group">
                                    <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100 font-outfit group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">Customer Behavior Insights</h3>
                                    <p className="text-slate-600 dark:text-slate-400 font-light leading-relaxed">
                                        Understand customer journeys, predict churn, identify upsell opportunities, and personalize experiences based on behavioral patterns.
                                    </p>
                                </div>
                                <div className="border-l-2 border-violet-500 pl-6 group">
                                    <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-slate-100 font-outfit group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">Operational Efficiency</h3>
                                    <p className="text-slate-600 dark:text-slate-400 font-light leading-relaxed">
                                        Monitor processes, identify bottlenecks, optimize resource allocation, and measure the impact of operational changes in real-time.
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* CTA */}
                        <motion.div 
                            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 p-8 sm:p-10 rounded-2xl text-center shadow-lg dark:shadow-none relative overflow-hidden"
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.1)_0%,_transparent_60%)] pointer-events-none" />
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4 font-outfit text-slate-950">Turn Your Data Into Your Competitive Advantage</h2>
                            <p className="text-base sm:text-lg mb-8 text-slate-900 opacity-90 max-w-xl mx-auto font-light">
                                See how AI-powered BI can unlock insights hidden in your data.
                            </p>
                            <motion.button
                                onClick={() => {
                                    navigate('/', { state: { scrollTo: 'contact' } });
                                }}
                                className="bg-slate-950 text-violet-400 hover:text-white px-8 py-3.5 rounded-full font-bold text-base shadow-xl border border-slate-900 transition duration-300"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Request a Demo
                            </motion.button>
                        </motion.div>
                    </div>
                </section>

                <Footer projectName={APP_CONFIG.projectName} />
            </div>
        </PageTransition>
    );
};

export default BusinessIntelligencePage;
